import { makeApi, Zodios, type ZodiosOptions } from '@zodios/core'
import { z } from 'zod'

const ChallengeStatusEnum = z.enum([
  'ongoing',
  'unbooted',
  'finished',
  'aborted',
])
const queryFilters = z
  .object({
    status: ChallengeStatusEnum,
    userId: z.string(),
    taskId: z.string(),
    assignmentIds: z.array(z.string()),
  })
  .partial()
  .passthrough()
  .optional()
const FeedbackStatusEnum = z.enum([
  'unprocessed',
  'processing1',
  'processing2',
  'processing3',
  'processing4',
  'processing5',
  'processing6',
  'processing7',
  'processing8',
  'processing9',
  'processing10',
  'processing11',
  'processing12',
  'processing13',
  'processing14',
  'processing15',
  'processed',
])
const PaneStateEnum = z.enum(['assignment', 'query', 'solution'])
const WorkspaceTabTypeEnum = z.enum([
  'assignment',
  'sql',
  'solution',
  'notes',
  'sqlCheatSheet',
  'schema',
])
const WorkspaceTab = z
  .object({ type: WorkspaceTabTypeEnum, data: z.string().optional() })
  .passthrough()
const WorkspaceTabView = z
  .object({
    activeTab: z.number(),
    tabs: z.array(WorkspaceTab),
    width: z.number().optional(),
  })
  .passthrough()
const WorkState = z
  .object({
    solution: z.string(),
    lastQuery: z.string(),
    notes: z.string(),
    paneState: z.array(PaneStateEnum),
    charts: z.record(
      z
        .object({
          id: z.string().optional(),
          chartType: z.enum(['table', 'line', 'bar', 'pie', 'scatter']),
          xAxis: z.string().optional(),
          yAxis: z.string().optional(),
          query: z.string(),
          data: z.array(z.object({}).partial().passthrough()).optional(),
          showTrendLine: z.boolean().optional(),
        })
        .passthrough()
    ),
    tabViews: z.array(WorkspaceTabView),
  })
  .partial()
  .passthrough()
const EmployerFeedback = z
  .object({ feedback: z.string(), grade: z.number() })
  .passthrough()
const UserFeedback = z
  .object({
    overallRating: z.number(),
    practiceNextRating: z.number(),
    agreeWithFeedback: z.boolean(),
    whyNoAgree: z.string().optional(),
    freeText: z.string().optional(),
    submissionDate: z.number().optional(),
  })
  .passthrough()
const QueryHistoryEntry = z
  .object({
    query: z.string(),
    resultCount: z.number().optional(),
    errorCode: z.string().optional(),
  })
  .passthrough()
const FeedbackLanguageEnum = z.enum(['en', 'he', 'es', 'fr', 'de'])
const Challenge = z
  .object({
    id: z.string(),
    taskId: z.string(),
    userId: z.string(),
    startTime: z.string().datetime({ offset: true }),
    submittableUntil: z.string().datetime({ offset: true }).optional(),
    workTime: z.number(),
    endTime: z.string().datetime({ offset: true }).optional(),
    lastWorkTimeUpdate: z.string().datetime({ offset: true }).optional(),
    status: ChallengeStatusEnum,
    feedbackStatus: FeedbackStatusEnum.optional(),
    workState: WorkState.optional(),
    feedbackText: z.string().optional(),
    feedbackGrade: z.number().optional(),
    employerFeedback: EmployerFeedback.optional(),
    slug: z.string(),
    userFeedback: UserFeedback.optional(),
    deleted: z.boolean().optional().default(false),
    assignmentId: z.string().optional(),
    challengeDisclaimer: z.string().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    queryHistory: z.array(QueryHistoryEntry).optional(),
    feedbackReleaseOverride: z.boolean().optional(),
    feedbackLanguage: FeedbackLanguageEnum.optional(),
  })
  .passthrough()
const language = z.enum(['SQL', 'Python'])
const ChallengeDifficultyEnum = z.enum([
  'Easy',
  'Intermediate',
  'Hard',
  'Expert',
])
const TaskTableQueriesExtension = z
  .object({ tableQueries: z.array(z.string()) })
  .partial()
  .passthrough()
const Task = z
  .object({
    name: z.string().min(3).max(200),
    body: z.string().min(10).max(50000),
    teaser: z.string().min(10).max(200),
    ownerId: z
      .string()
      .min(1)
      .max(50)
      .regex(/^[a-zA-Z0-9_-]+$/)
      .optional(),
    language: language,
    difficulty: ChallengeDifficultyEnum,
    description: z.string().min(20).max(10000),
    defaultQuery: z.string().min(1).max(2000),
    dataQuery: z.string().min(1).max(2000),
    seedFactor: z.number(),
    feedbackPayload: z.string().min(2).optional(),
    dataBank: z.string().min(2),
    accessGroups: z
      .array(
        z
          .string()
          .min(1)
          .max(50)
          .regex(/^[a-zA-Z0-9_-]+$/)
      )
      .min(1)
      .max(10),
    proficiencies: z
      .array(
        z.enum([
          'joins',
          'dataSanitization',
          'anomalyDetection',
          'timeSeriesAnalysis',
          'windowFunctions',
          'aggregation',
        ])
      )
      .optional(),
    solutionPlaceholder: z.string().optional(),
    availabilityWeight: z.number().optional(),
    deleted: z.boolean().optional(),
    sharedDBName: z.string().optional(),
    customQueryTimeout: z.number().gte(1).lte(255).optional(),
    slug: z.string().optional(),
  })
  .passthrough()
  .and(z.object({ id: z.string() }).passthrough())
  .and(TaskTableQueriesExtension)
const ChallengeInfo = z
  .object({
    challenge: Challenge,
    task: Task,
    user: z
      .object({
        userId: z.string(),
        fullName: z.string(),
        role: z.enum(['admin', 'user', 'partner']),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()
const ChallengeErrorCode = z.enum([
  'challengeNotFound',
  'badRequest',
  'invalidChallengeId',
  'challengeNotOngoing',
  'challengeInCooldown',
  'databaseCreationFailed',
  'databasePopulationFailed',
  'databaseConnectionFailed',
  'taskNotFound',
  'unauthorized',
  'loggedOut',
  'unknownError',
  'invalidAssignment',
  'queryTimeout',
  'disallowedPatterns',
  'timeWindowEnded',
])
const ErrorResponse = z
  .object({ error: z.string(), errorCode: ChallengeErrorCode.optional() })
  .passthrough()
const FeedbackStatusFilterOption = z.enum([
  'no-feedback',
  'unprocessed',
  'processing',
  'processed',
])
const ChallengeListFilter = z
  .object({
    status: z.array(ChallengeStatusEnum),
    feedbackStatus: z.array(FeedbackStatusFilterOption),
    userId: z.array(z.string()),
    taskId: z.array(z.string()),
    assignmentIds: z.array(z.string()),
  })
  .partial()
  .passthrough()
const ChallengeSortFieldEnum = z.enum([
  'id',
  'userId',
  'taskId',
  'status',
  'feedbackStatus',
  'workTime',
  'feedbackGrade',
  'startTime',
  'endTime',
  'lastWorkTimeUpdate',
  'feedbackReleaseOverride',
])
const SortDirectionEnum = z.enum(['asc', 'desc'])
const ListChallengesRequest = z
  .object({
    filter: ChallengeListFilter,
    sortField: ChallengeSortFieldEnum,
    sortDirection: SortDirectionEnum,
    limit: z.number().int().gte(1),
    offset: z.number().int().gte(0),
  })
  .partial()
  .passthrough()
const CountResponse = z.object({ count: z.number() }).passthrough()
const AvailableChallengeStatusEnum = z.enum([
  'available',
  'unbooted',
  'ongoing',
  'cooldown',
  'aborted',
  'finished',
])
const TimeCategoryEnum = z.enum(['New', 'upTo30', 'upTo60', '60OrMore'])
const AvailableChallenge = z
  .object({
    challengeId: z.string().optional(),
    slug: z.string(),
    taskId: z.string(),
    taskSlug: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    language: z.enum(['SQL', 'Python']),
    difficulty: ChallengeDifficultyEnum,
    status: AvailableChallengeStatusEnum,
    cooldownRemainingTime: z.number().optional(),
    teaser: z.string(),
    proficiencies: z.array(z.string()).optional(),
    assignmentId: z.string().optional(),
    timeCategory: TimeCategoryEnum,
  })
  .passthrough()
const AvailableAssignment = z
  .object({
    challengeId: z.string().optional(),
    slug: z.string().optional(),
    taskId: z.string(),
    taskSlug: z.string().optional(),
    name: z.string(),
    description: z.string().optional(),
    language: language,
    status: AvailableChallengeStatusEnum,
    teaser: z.string().optional(),
    organizationId: z.string(),
    organizationName: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    overdueDate: z.string().optional(),
    timeLimit: z.number().optional(),
    assignmentId: z.string(),
  })
  .passthrough()
const ChallengesResponse = z
  .object({
    count: z.number(),
    availableChallenges: z.array(AvailableChallenge),
    availableAssignments: z.array(AvailableAssignment),
    hasFinishedAssignment: z.boolean().optional(),
  })
  .passthrough()
const DeleteChallengeResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const BootEnvironmentRequest = z
  .object({ taskId: z.string(), assignmentId: z.string().optional() })
  .passthrough()
const BootEnvironmentResponse = z
  .object({ success: z.boolean(), slug: z.string() })
  .passthrough()
const RunQueryRequest = z
  .object({ challengeId: z.string(), query: z.string() })
  .passthrough()
const RunQueryResponse = z
  .object({
    results: z.array(z.record(z.union([z.string(), z.number()]))),
    error: z.string(),
    hint: z.string(),
  })
  .partial()
  .passthrough()
const SaveWorkRequest = z
  .object({ challengeId: z.string(), workState: WorkState.optional() })
  .passthrough()
const SaveWorkResponse = z
  .object({ success: z.boolean() })
  .partial()
  .passthrough()
const SubmitSolutionRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const SubmitSolutionResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const SubmitUserFeedbackRequest = z
  .object({ challengeId: z.string(), userFeedback: UserFeedback })
  .passthrough()
const SubmitUserFeedbackResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const ReportWorkTimeRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const ReportWorkTimeResponse = z.object({ workTime: z.number() }).passthrough()
const RollChallengeResponse = z.object({ taskId: z.string() }).passthrough()
const RetakeChallengeRequest = z
  .object({ challengeId: z.string() })
  .passthrough()
const RetakeChallengeResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()
const ReleaseFeedbackRequest = z
  .object({ challengeId: z.string(), release: z.boolean() })
  .passthrough()
const ReleaseFeedbackResponse = z
  .object({ success: z.boolean(), message: z.string().optional() })
  .passthrough()

export const schemas = {
  ChallengeStatusEnum,
  queryFilters,
  FeedbackStatusEnum,
  PaneStateEnum,
  WorkspaceTabTypeEnum,
  WorkspaceTab,
  WorkspaceTabView,
  WorkState,
  EmployerFeedback,
  UserFeedback,
  QueryHistoryEntry,
  FeedbackLanguageEnum,
  Challenge,
  language,
  ChallengeDifficultyEnum,
  TaskTableQueriesExtension,
  Task,
  ChallengeInfo,
  ChallengeErrorCode,
  ErrorResponse,
  FeedbackStatusFilterOption,
  ChallengeListFilter,
  ChallengeSortFieldEnum,
  SortDirectionEnum,
  ListChallengesRequest,
  CountResponse,
  AvailableChallengeStatusEnum,
  TimeCategoryEnum,
  AvailableChallenge,
  AvailableAssignment,
  ChallengesResponse,
  DeleteChallengeResponse,
  BootEnvironmentRequest,
  BootEnvironmentResponse,
  RunQueryRequest,
  RunQueryResponse,
  SaveWorkRequest,
  SaveWorkResponse,
  SubmitSolutionRequest,
  SubmitSolutionResponse,
  SubmitUserFeedbackRequest,
  SubmitUserFeedbackResponse,
  ReportWorkTimeRequest,
  ReportWorkTimeResponse,
  RollChallengeResponse,
  RetakeChallengeRequest,
  RetakeChallengeResponse,
  ReleaseFeedbackRequest,
  ReleaseFeedbackResponse,
}

const endpoints = makeApi([
  {
    method: 'get',
    path: '/',
    alias: 'getAllChallenges',
    description: `Retrieves all challenges with optional filtering, sorting and pagination`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'queryFilters',
        type: 'Query',
        schema: queryFilters,
      },
      {
        name: 'sortField',
        type: 'Query',
        schema: z
          .enum([
            'id',
            'userId',
            'taskId',
            'status',
            'feedbackStatus',
            'workTime',
            'feedbackGrade',
            'startTime',
            'endTime',
            'lastWorkTimeUpdate',
            'feedbackReleaseOverride',
          ])
          .optional(),
      },
      {
        name: 'sortDirection',
        type: 'Query',
        schema: z.enum(['asc', 'desc']).optional(),
      },
      {
        name: 'limit',
        type: 'Query',
        schema: z.number().int().gte(1).optional(),
      },
      {
        name: 'offset',
        type: 'Query',
        schema: z.number().int().gte(0).optional(),
      },
    ],
    response: z.array(ChallengeInfo),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'delete',
    path: '/:id',
    alias: 'deleteChallenge',
    description: `Soft-deletes a challenge by setting the deleted flag to true. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'id',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: DeleteChallengeResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/:slug',
    alias: 'getChallengeBySlug',
    description: `Retrieves a specific challenge by its slug`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'slug',
        type: 'Path',
        schema: z.string(),
      },
    ],
    response: ChallengeInfo,
    errors: [
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/available-challenges',
    alias: 'getAvailableChallenges',
    description: `Retrieves a list of all available challenges`,
    requestFormat: 'json',
    response: ChallengesResponse,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/boot',
    alias: 'bootEnvironment',
    description: `Boots the environment for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: BootEnvironmentRequest,
      },
    ],
    response: BootEnvironmentResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/count',
    alias: 'getChallengeCount',
    description: `Retrieves the count of challenges filtered by status`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'status',
        type: 'Query',
        schema: z.enum(['ongoing', 'unbooted', 'finished', 'aborted']),
      },
    ],
    response: z.object({ count: z.number() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request - status parameter required`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/feedback-pdf',
    alias: 'getFeedbackPdf',
    description: `Generates and returns a PDF document with the candidate&#x27;s feedback for a given challenge. Requires partner access; verifies the challenge&#x27;s assignment belongs to the partner&#x27;s organization.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'challengeId',
        type: 'Query',
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Bad request - missing challengeId or challenge has no feedback`,
        schema: ErrorResponse,
      },
      {
        status: 403,
        description: `Forbidden - challenge does not belong to partner&#x27;s organization`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/finished-challenges',
    alias: 'getFinishedChallenges',
    description: `Retrieves all finished challenges for the current user`,
    requestFormat: 'json',
    response: z.array(ChallengeInfo),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/list',
    alias: 'listChallenges',
    description: `Retrieves challenges with a structured filter object plus pagination and sort. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ListChallengesRequest,
      },
    ],
    response: z.array(ChallengeInfo),
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/ongoing-challenges',
    alias: 'getOngoingChallenges',
    description: `Retrieves all ongoing challenges for the logged-in user with their task details`,
    requestFormat: 'json',
    response: ChallengesResponse,
    errors: [
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/query',
    alias: 'runQuery',
    description: `Executes a query for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: RunQueryRequest,
      },
    ],
    response: RunQueryResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid parameters`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/release-feedback',
    alias: 'releaseFeedback',
    description: `Toggles manual feedback release override for a specific challenge. Admin only.`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: ReleaseFeedbackRequest,
      },
    ],
    response: ReleaseFeedbackResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or payload`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/report-worktime',
    alias: 'reportWorkTime',
    description: `Reports work time for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: z.object({ workTime: z.number() }).passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/retake',
    alias: 'retakeChallenge',
    description: `Restores a challenge to ongoing status, resets end date and clears feedback data`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: RetakeChallengeResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'get',
    path: '/roll-challenge',
    alias: 'rollChallenge',
    description: `Returns a random task ID from existing available tasks`,
    requestFormat: 'json',
    response: z.object({ taskId: z.string() }).passthrough(),
    errors: [
      {
        status: 404,
        description: `No available tasks found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/save-work',
    alias: 'saveWork',
    description: `Saves the current work progress for a challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SaveWorkRequest,
      },
    ],
    response: z.object({ success: z.boolean() }).partial().passthrough(),
    errors: [
      {
        status: 400,
        description: `Bad request - invalid parameters`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/submit-solution',
    alias: 'submitSolution',
    description: `Submits a solution for a specific challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: z.object({ challengeId: z.string() }).passthrough(),
      },
    ],
    response: SubmitSolutionResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or solution`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Route not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
  {
    method: 'post',
    path: '/submit-user-feedback',
    alias: 'submitUserFeedback',
    description: `Submits user feedback for a completed challenge`,
    requestFormat: 'json',
    parameters: [
      {
        name: 'body',
        type: 'Body',
        schema: SubmitUserFeedbackRequest,
      },
    ],
    response: SubmitUserFeedbackResponse,
    errors: [
      {
        status: 400,
        description: `Bad request - invalid challengeId or feedback payload`,
        schema: ErrorResponse,
      },
      {
        status: 404,
        description: `Challenge not found`,
        schema: ErrorResponse,
      },
      {
        status: 500,
        description: `Internal server error`,
        schema: ErrorResponse,
      },
    ],
  },
])

export const api = new Zodios(endpoints)

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options)
}
